/* CloudGalleryPicker — universal image source for the platform.
 * Upload from device (saved to the cloud gallery forever) OR reuse any
 * previously uploaded / snapshot / AI-generated asset — no re-uploading,
 * no wasted AI credits. */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, UploadCloud, Search, Loader2, Trash2, Camera, Sparkles, ImagePlus } from "lucide-react";

const KINDS = [
  { key: "", label: "All" },
  { key: "upload", label: "Uploads" },
  { key: "snapshot", label: "Snapshots" },
  { key: "ai", label: "AI images" },
];
const BADGE = {
  upload: { label: "Upload", color: "#0EA5E9", icon: UploadCloud },
  snapshot: { label: "Snapshot", color: "#22D3EE", icon: Camera },
  ai: { label: "AI", color: "#7C3AED", icon: Sparkles },
};

export default function CloudGalleryPicker({ open, onClose, onSelect }) {
  const fileRef = useRef(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/gallery", { params: { kind } });
        if (!cancelled) setAssets(data.assets || []);
      } catch { if (!cancelled) toast.error("Could not load gallery"); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, kind]);

  if (!open) return null;

  const filtered = assets.filter((a) => !q || (a.title || "").toLowerCase().includes(q.toLowerCase()));

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Image files only");
    if (f.size > 10 * 1024 * 1024) return toast.error("Max 10 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Saved to your cloud gallery");
      onSelect?.(data.asset.url, data.asset.title);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const del = async (a, ev) => {
    ev.stopPropagation();
    try {
      await api.delete(`/gallery/${a.id}`);
      setAssets((cur) => cur.filter((x) => x.id !== a.id));
    } catch { toast.error("Delete failed"); }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[97] bg-black/75 backdrop-blur-md flex items-center justify-center px-4"
      data-testid="cloud-gallery-picker"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-3xl bg-[#0B0F17] border border-[#0EA5E9]/30 rounded-2xl shadow-[0_0_80px_-20px_rgba(14,165,233,0.55)] overflow-hidden text-white">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-[#0EA5E9]/10 to-transparent">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#38BDF8] font-bold inline-flex items-center gap-1.5">
              <ImagePlus size={11} /> / cloud gallery
            </div>
            <div className="font-display font-bold text-lg tracking-tight mt-0.5">Pick an image or upload a new one</div>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="gallery-upload-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0EA5E9] text-white text-xs font-bold hover:bg-[#0284C7] disabled:opacity-60 transition-colors"
            >
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />}
              {uploading ? "Uploading…" : "Upload from device"}
            </button>
            <button onClick={onClose} data-testid="gallery-close" className="text-white/60 hover:text-white p-1 rounded hover:bg-white/10"><X size={16} /></button>
          </div>
        </div>

        <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2 flex-wrap">
          {KINDS.map((k) => (
            <button key={k.key} onClick={() => setKind(k.key)} data-testid={`gallery-kind-${k.key || "all"}`}
              className={`text-[11px] font-semibold px-3 py-1.5 rounded-full border transition-colors ${kind === k.key ? "bg-white text-[#0A0C10] border-white" : "border-white/20 text-white/60 hover:text-white hover:border-white/50"}`}>
              {k.label}
            </button>
          ))}
          <div className="flex-1" />
          <div className="flex items-center gap-1.5 bg-white/5 border border-white/15 rounded-md px-2.5 py-1.5 min-w-[180px]">
            <Search size={12} className="text-white/40 shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="gallery-search"
              className="flex-1 min-w-0 bg-transparent text-xs outline-none text-white placeholder:text-white/30" />
          </div>
        </div>

        <div className="p-5 max-h-[56vh] overflow-y-auto">
          {loading ? (
            <div className="py-12 text-center text-sm text-white/50 inline-flex w-full justify-center items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading your gallery…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-white/50">
              Nothing here yet — upload an image or generate one with AI and it will live here forever.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filtered.map((a) => {
                const b = BADGE[a.kind] || BADGE.upload;
                const BIcon = b.icon;
                return (
                  <button
                    key={a.id}
                    onClick={() => onSelect?.(a.url, a.title)}
                    data-testid={`gallery-asset-${a.id}`}
                    className="group relative text-left border border-white/10 hover:border-[#0EA5E9] rounded-lg overflow-hidden bg-black/40 transition-colors"
                  >
                    <div className="aspect-square bg-black/60">
                      <img src={a.url} alt={a.title} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                    <div className="px-2 py-1.5">
                      <div className="text-[11px] font-medium truncate">{a.title}</div>
                      <div className="text-[9px] font-mono uppercase tracking-wider mt-0.5 inline-flex items-center gap-1" style={{ color: b.color }}>
                        <BIcon size={9} /> {b.label}
                      </div>
                    </div>
                    {a.kind !== "ai" && (
                      <span
                        onClick={(ev) => del(a, ev)}
                        data-testid={`gallery-del-${a.id}`}
                        title="Delete from gallery"
                        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-md bg-black/70 text-red-400 hover:text-red-300 items-center justify-center hidden group-hover:flex"
                      ><Trash2 size={11} /></span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
